import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { Send, Smile, Paperclip, Loader2, X, AlertTriangle, Image as ImageIcon, FileText, Hash, Check, ChevronRight, Pencil, Search, StickyNote, UserCheck, Clock, AtSign, Workflow, StopCircle, Ban } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toContactId } from '@/lib/utils/phone';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { typingLabel, type PresenceUser } from '@/lib/firebase/chat-presence';
import { ChatPresenceAvatars } from './chat-presence-avatars';
import { useMessageStore } from '@/stores/message-store';
import { useModuleStore } from '@/stores/module-store';
import { useConversationStore } from '@/stores/conversation-store';
import { useContainerSize } from '@/hooks/use-container-size';
import { useUIStore } from '@/stores/ui-store';
import { useTemplates } from '@/hooks/use-templates';
import { useSavedTemplates } from '@/hooks/use-saved-templates';
import { useTextSelectionToolbar } from '@/hooks/use-text-selection-toolbar';
import { TemplateDialog, VariableFillingDialog } from '@/components/messaging/template-picker';
import { TextFormattingToolbar } from '@/components/common/text-formatting-toolbar';
import { ConvAssigneePicker } from './conv-assignee-picker';
import { SchedulePickerPopover } from '@/components/messaging/schedule-picker';
import { formatScheduleDate } from '@/lib/utils/schedule';
import { uploadAttachment, deleteFile } from '@/lib/firebase/storage';
import { toast } from 'sonner';
import { resolveTemplateVariables } from '@/lib/utils/message';
import { OPT_OUT_SMS_WARNINGS, appendOptOutFooter, isOptOutSmsChannel } from '@/lib/utils/opt-out';
import { OptOutSmsWarning } from '@/components/messaging/optout-sms-warning';
import { useAppStore } from '@/stores/app-store';
import { useAuthStore } from '@/stores/auth-store';
import { useAppUsersStore } from '@/stores/app-users-store';
import { useSettingsStore } from '@/stores/settings-store';
import { usePermissions, senderPermissionAllowsSend, getSenderPermissionStandalone } from '@/hooks/use-permissions';
import type { Conversation, Message, MessageAttachment, MessageTemplate, Sender } from '@/types/messaging';


const EMOJI_CATEGORIES = [
  {
    label: 'Smileys & People',
    emojis: [
      '😊', '😂', '🤣', '😁', '😄', '😅', '😆', '😉', '😋', '😎',
      '🥰', '😍', '🤩', '😘', '😗', '😙', '🥲', '😜', '🤪', '😝',
      '🤗', '🤭', '🤫', '🤔', '🫡', '🤐', '🤨', '😏', '😌', '😔',
      '😪', '😴', '🤤', '😷', '🤒', '🤕', '🤢', '🤮', '🥵', '🥶',
      '😵', '🤯', '🥳', '😱', '😨', '😰', '😢', '😭', '😤', '😡',
      '🤬', '😈', '👿', '💀', '☠️', '💩', '🤡', '👹', '👻', '👽',
      '🤖', '😺', '😸', '😻', '🙀', '😿', '🙈', '🙉', '🙊',
    ],
  },
  {
    label: 'Gestures & Body',
    emojis: [
      '👋', '🤚', '🖐️', '✋', '🖖', '👌', '🤌', '🤏', '✌️', '🤞',
      '🫰', '🤟', '🤘', '🤙', '👈', '👉', '👆', '👇', '☝️', '🫵',
      '👍', '👎', '✊', '👊', '🤛', '🤜', '👏', '🙌', '🫶', '👐',
      '🤲', '🤝', '🙏', '💪', '🦾', '🖕', '✍️', '🤳', '💅',
    ],
  },
  {
    label: 'Hearts & Symbols',
    emojis: [
      '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔',
      '❤️‍🔥', '💕', '💞', '💓', '💗', '💖', '💘', '💝', '💟', '❣️',
      '💯', '💢', '💥', '💫', '💦', '💨', '🕳️', '💣', '💬', '💭',
      '🔥', '✨', '🌟', '⭐', '🌈', '☀️', '🌙', '💤', '🎵', '🎶',
    ],
  },
  {
    label: 'Business & Objects',
    emojis: [
      '📞', '📱', '💼', '📧', '📋', '✅', '❌', '⏰', '📅', '🔔',
      '💡', '🎯', '📊', '💬', '🔗', '📌', '🏷️', '💰', '💵', '💳',
      '📝', '📄', '📁', '📂', '🗂️', '📇', '📈', '📉', '🗓️', '📆',
      '🔒', '🔓', '🔑', '🔍', '🔎', '🔧', '⚙️', '🛠️', '📎', '🖊️',
      '✏️', '📐', '📏', '🗑️', '📦', '📮', '📩', '📨', '💻', '🖥️',
    ],
  },
  {
    label: 'Celebrations',
    emojis: [
      '🎉', '🎊', '🎈', '🎁', '🎂', '🎄', '🎃', '🪅', '🎆', '🎇',
      '🧨', '🎗️', '🎀', '🎖️', '🏆', '🏅', '🥇', '🥈', '🥉', '🎪',
      '🎭', '🎨', '🎬', '🎤', '🎧', '🎷', '🎸', '🎹', '🥁', '🪘',
    ],
  },
  {
    label: 'Food & Drink',
    emojis: [
      '☕', '🍵', '🧃', '🥤', '🍺', '🍻', '🥂', '🍷', '🍸', '🍹',
      '🍕', '🍔', '🍟', '🌮', '🌯', '🍣', '🍱', '🍩', '🍪', '🎂',
      '🍰', '🧁', '🍫', '🍬', '🍭', '🍿', '🥐', '🥖', '🥞', '🧇',
    ],
  },
  {
    label: 'Nature & Animals',
    emojis: [
      '🐶', '🐱', '🐭', '🐰', '🦊', '🐻', '🐼', '🐨', '🦁', '🐮',
      '🐷', '🐸', '🐵', '🐔', '🐧', '🐦', '🦅', '🦋', '🐛', '🐝',
      '🌸', '🌺', '🌻', '🌹', '🌷', '🌱', '🌲', '🌳', '🍀', '🍃',
    ],
  },
  {
    label: 'Travel & Places',
    emojis: [
      '🚗', '🚕', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '✈️', '🚀',
      '🛸', '🚁', '⛵', '🚢', '🏠', '🏢', '🏥', '🏫', '🏰', '🗼',
      '🗽', '⛪', '🕌', '🛕', '🌍', '🌎', '🌏', '🗺️', '🏔️', '⛰️',
    ],
  },
];

/**
 * Read an image file's intrinsic pixel size. Resolves null for anything the
 * browser can't decode, so a corrupt or exotic image never blocks the note.
 */
function readImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const size = img.naturalWidth && img.naturalHeight
        ? { width: img.naturalWidth, height: img.naturalHeight }
        : null;
      URL.revokeObjectURL(objectUrl);
      resolve(size);
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(null);
    };
    img.src = objectUrl;
  });
}

interface ChatInputProps {
  conversation: Conversation;
  onMessageSent?: () => void;
  /** Message being replied to — shows quote preview above input */
  replyingTo?: Message | null;
  /** Callback to cancel the reply */
  onCancelReply?: () => void;
  /** KAN-155: the customer opted out and the agent has not picked "Send anyway"
   *  for this opt-out. Owned by the chat thread, which locks Retry with it too. */
  optOutLocked: boolean;
  /** The agent confirmed "Send anyway" — unlocks this chat for this opt-out. */
  onOptOutSendAnyway: () => void;
  /** Other agents currently in this chat (from useChatPresence) — drives the
   *  "X is typing a reply…" strip above the composer. */
  presenceOthers?: PresenceUser[];
  /** True when the chat header (which normally carries the presence avatars)
   *  is hidden — e.g. the CRM widget — so the avatars + "also here" text move
   *  into the strip above the composer instead. */
  showPresenceInline?: boolean;
  /** Tell presence I am typing (true on keystroke, false when the box is
   *  empty or a message was sent). Idempotent, safe to call often. */
  onTypingChange?: (typing: boolean) => void;
}

export function ChatInput({ conversation, onMessageSent, replyingTo, onCancelReply, optOutLocked, onOptOutSendAnyway, presenceOthers, showPresenceInline, onTypingChange }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [attachedFile, setAttachedFile] = useState<{ file: File; previewUrl: string; mediaUrl?: string; uploading: boolean } | null>(null);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showVariableDialog, setShowVariableDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<MessageTemplate | null>(null);
  const [templateVariables, setTemplateVariables] = useState<Record<string, string>>({});
  const [showEmoji, setShowEmoji] = useState(false);
  const [noteMode, setNoteMode] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [recording, setRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);
  const tpaModule = useModuleStore((s) => s.tpaModule);
  const saasModule = useModuleStore((s) => s.saasModule);
  const sender = useMessageStore((s) => s.sender);
  const setSender = useMessageStore((s) => s.setSender);
  const addMessage = useConversationStore((s) => s.addMessage);
  const addNote = useConversationStore((s) => s.addNote);
  const markMessageAsReadInConversation = useConversationStore((s) => s.markMessageAsReadInConversation);
  const markAsRead = useConversationStore((s) => s.markAsRead);
  const realtimeMessageIds = useConversationStore((s) => s.realtimeMessageIds);
  const clearRealtimeMessageIds = useConversationStore((s) => s.clearRealtimeMessageIds);
  const updateConvAssignee = useConversationStore((s) => s.updateConvAssignee);
  const stopActiveFlow = useConversationStore((s) => s.stopActiveFlow);
  const messages = useConversationStore((s) => s.messages);
  const saveDraft = useConversationStore((s) => s.saveDraft);
  const clearDraft = useConversationStore((s) => s.clearDraft);
  const getDraft = useConversationStore((s) => s.getDraft);
  const currentUser = useAuthStore((s) => s.user);
  const { getSenderPermission, permissionsReady } = usePermissions();
  const senderPerm = sender ? getSenderPermission(sender.id) : 'view_and_send';
  // permissionsReady guards the load window: before the team snapshot arrives a
  // shared user would otherwise resolve the permissive fallback and could send
  // against a rule that is about to load (KAN-14). Admins are always ready.
  const canSend = permissionsReady && senderPermissionAllowsSend(senderPerm);
  /** Internal notes are stored in Firestore only — they never reach the
   *  customer — so a 'view_only' user may still write them. Only outbound
   *  delivery requires canSend. */
  const isInternalNote = () =>
    noteMode && !!(message.trim() || attachedFile || useMessageStore.getState().attachedFile);

  // Settings › Chat View › Input / Display
  // Enter to Send (default on): Enter sends, Shift+Enter inserts a newline.
  // Off: Enter inserts a newline, Shift+Enter sends.
  const enterToSend = useSettingsStore((s) => s.preferences.chatEnterToSend !== false);
  // Emoji Picker (default on): hides the 😊 button + popover in the composer.
  const showEmojiPicker = useSettingsStore((s) => s.preferences.showEmojiPicker !== false);

  // Replies must leave from the conversation's own number (conv id =
  // senderNumber::contactNumber). If the globally selected sender is a
  // different number — e.g. the default/test sender auto-selected before the
  // senders list finished loading — resolve the conversation's sender before
  // sending, so the message never goes out from the wrong (possibly test) number.
  const resolveConversationSender = useCallback(async (): Promise<Sender | null> => {
    if (!tpaModule || !sender) return sender;
    const convSenderNumber = (conversation.id || '').split('::')[0] || '';
    const convSenderDigits = convSenderNumber.replace(/\D/g, '');
    if (!convSenderDigits || !conversation.id.includes('::')) return sender;
    const matchesConv = (s: Sender): boolean => {
      const sDigits = (s.phoneNumber || '').replace(/\D/g, '');
      const sMsisdn = (s.data?.msisdn as string) || '';
      if (sDigits !== convSenderDigits && sMsisdn !== convSenderDigits) return false;
      return !conversation.channel || !s.channel || s.channel === conversation.channel;
    };
    if (matchesConv(sender)) return sender;
    try {
      const allSenders = await tpaModule.getSenders();
      const matched = allSenders.find(matchesConv);
      if (matched) {
        setSender(matched);
        return matched;
      }
    } catch { /* senders unavailable — fall through to the selected sender */ }
    return sender;
  }, [tpaModule, sender, conversation.id, conversation.channel, setSender]);

  const isPlaceholder = conversation.contactName.startsWith('Start ') && conversation.contactName.endsWith(' conversation');
  const { isMini } = useContainerSize();

  // Check if the conversation is assigned to another agent
  const assignedToOther = !!(
    conversation.assigneeEmail &&
    conversation.assigneeEmail !== 'unassigned' &&
    currentUser?.email &&
    conversation.assigneeEmail !== currentUser.email
  );

  // Chat Automation is actively driving this conversation (paused mid-flow).
  // While set, the composer is locked — the agent must stop the flow to take
  // over manually. Internal notes stay allowed (they don't reach the customer).
  const activeFlow = conversation.activeFlow;
  const [stoppingFlow, setStoppingFlow] = useState(false);
  const handleStopFlow = async () => {
    if (stoppingFlow) return;
    setStoppingFlow(true);
    try {
      await stopActiveFlow(conversation.id);
    } finally {
      setStoppingFlow(false);
    }
  };

  // KAN-155: the customer opted out (replied STOP) — the composer is locked
  // (`optOutLocked`, from the chat thread) until the agent picks "Send anyway".
  // Internal notes stay allowed (they never reach the customer).
  const [confirmSendAnyway, setConfirmSendAnyway] = useState(false);
  useEffect(() => {
    // A half-open "Send anyway?" never carries over to another chat.
    setConfirmSendAnyway(false);
  }, [conversation.id]);

  // Restore saved draft when conversation changes
  const prevConvIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (conversation.id !== prevConvIdRef.current) {
      prevConvIdRef.current = conversation.id;
      const draft = getDraft(conversation.id);
      if (draft) {
        setMessage(draft);
        // Auto-resize textarea after restoring draft
        requestAnimationFrame(() => {
          const el = textareaRef.current;
          if (el) {
            el.style.height = 'auto';
            el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
          }
        });
      } else {
        setMessage('');
      }
    }
  }, [conversation.id, getDraft]);

  // Auto-focus textarea when replying to a message
  useEffect(() => {
    if (replyingTo) {
      textareaRef.current?.focus();
    }
  }, [replyingTo]);

  const isWhatsApp = conversation.channel === 'WHATSAPP';
  // Lazy mode: only load from IDB cache on mount (no API calls during conversation loading).
  // Full fetch triggers when template picker opens via refresh().
  const { templates: tpaTemplates, loading: tpaTemplatesLoading, refresh: refreshTemplates, forceRefresh: forceRefreshTemplates } = useTemplates(conversation.channel, { lazy: true });
  // Open template dialog + trigger lazy template fetch (first open makes API calls)
  const openTemplateDialog = useCallback(() => {
    setShowTemplateDialog(true);
    refreshTemplates();
  }, [refreshTemplates]);
  // Custom saved templates (Firebase) — available for ALL channels
  const { templates: savedTemplates, loading: savedTemplatesLoading, saveTemplate, deleteTemplate } = useSavedTemplates();
  // Merge saved + TPA provider templates.
  // WhatsApp gets all provider templates; SMS gets only SMS-compatible ones
  // (Twilio twilio/text content templates, marked channel === 'SMS').
  const templates = useMemo(() => {
    const all = [...savedTemplates];
    if (isWhatsApp) {
      all.push(...tpaTemplates);
    } else {
      all.push(...tpaTemplates.filter((t) => t.channel === 'SMS'));
    }
    return all;
  }, [isWhatsApp, tpaTemplates, savedTemplates]);
  const templatesLoading = tpaTemplatesLoading || savedTemplatesLoading;

  // # placeholder autocomplete state
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const [placeholderFilter, setPlaceholderFilter] = useState('');
  const [placeholderSearch, setPlaceholderSearch] = useState('');
  const [placeholderTriggerIndex, setPlaceholderTriggerIndex] = useState(-1);
  const [selectedPropIndex, setSelectedPropIndex] = useState(0);
  const placeholderDropdownRef = useRef<HTMLDivElement>(null);
  const placeholderSearchRef = useRef<HTMLInputElement>(null);

  // @ mention autocomplete state (only active in note mode)
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionTriggerIndex, setMentionTriggerIndex] = useState(-1);
  const [selectedMentionIndex, setSelectedMentionIndex] = useState(0);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);
  const orgUsers = useAppUsersStore((s) => s.orgUsers);

  const filteredMentionUsers = useMemo(() => {
    const users = Object.values(orgUsers);
    if (!mentionFilter) return users;
    const lower = mentionFilter.toLowerCase();
    return users.filter(
      (u) => u.name?.toLowerCase().includes(lower) || u.email.toLowerCase().includes(lower)
    );
  }, [orgUsers, mentionFilter]);

  const insertMention = useCallback((email: string, name: string) => {
    const el = textareaRef.current;
    if (!el) return;
    const mentionText = `@${name || email.split('@')[0]}`;
    const before = message.substring(0, mentionTriggerIndex);
    const after = message.substring(el.selectionStart);
    const newVal = `${before}${mentionText} ${after}`;
    setMessage(newVal);
    setShowMentions(false);
    setMentionFilter('');
    setSelectedMentionIndex(0);
    // Set cursor after the mention
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = before.length + mentionText.length + 1;
        textareaRef.current.setSelectionRange(pos, pos);
        textareaRef.current.focus();
      }
    });
  }, [message, mentionTriggerIndex]);

  // Compute available properties + their values from conversation contact or CRM recipients
  const recipients = useMessageStore((s) => s.recipients);
  const crmRecordContext = useUIStore((s) => s.crmRecordContext);
  const activeView = useUIStore((s) => s.activeView);
  const crmModuleFields = useUIStore((s) => s.crmModuleFields);

  /** Data types that produce meaningful text values for message placeholders */
  const PLACEHOLDER_DATA_TYPES = new Set([
    'text', 'phone', 'email', 'number', 'currency', 'double',
    'date', 'datetime', 'website', 'textarea', 'bigint', 'autonumber',
    'integer', 'picklist', 'multiselectpicklist', 'lookup', 'ownerlookup',
  ]);

  /** Set of field apiNames that have a meaningful data type (for filtering) */
  const meaningfulFieldNames = useMemo(() => {
    if (crmModuleFields.length === 0) return null; // null = no filtering, show all
    const allowed = new Set<string>();
    for (const f of crmModuleFields) {
      if (PLACEHOLDER_DATA_TYPES.has(f.dataType)) {
        allowed.add(f.apiName);
      }
    }
    // Always allow standard fields
    allowed.add('name');
    allowed.add('phone');
    return allowed;
  }, [crmModuleFields]);

  /** Map of field apiName → display label from CRM module schema */
  const fieldDisplayLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const f of crmModuleFields) {
      map[f.apiName] = f.displayLabel;
    }
    return map;
  }, [crmModuleFields]);

  const crmSelectedPhoneField = useUIStore((s) => s.crmSelectedPhoneField);
  const crmPhoneFields = useUIStore((s) => s.crmPhoneFields);

  /**
   * Resolve the CRM recipient that matches the currently selected phone field.
   * In multi-record context the phone field key is "{recordId}_{apiName}",
   * so we extract the recordId and match by entityId.
   * In single-record context we just pick the first CRM recipient.
   */
  const activeCrmRecipient = useMemo(() => {
    const crmRecipients = recipients.filter((r) => r.source === 'crm');
    if (crmRecipients.length === 0) return null;
    if (crmRecipients.length === 1) return crmRecipients[0];

    // Multi-record: extract recordId from the selected phone field key
    if (crmSelectedPhoneField) {
      const selectedField = crmPhoneFields.find(
        (f) => (f.key || f.apiName) === crmSelectedPhoneField
      );
      if (selectedField?.key) {
        // key format: "{recordId}_{apiName}" — extract recordId
        const underscoreIdx = selectedField.key.lastIndexOf('_');
        if (underscoreIdx > 0) {
          const recordId = selectedField.key.substring(0, underscoreIdx);
          const match = crmRecipients.find((r) => r.entityId === recordId);
          if (match) return match;
        }
      }
      // Fallback: match by phone number
      if (selectedField?.value) {
        const cleanPhone = selectedField.value.replace(/\D/g, '');
        const match = crmRecipients.find((r) => {
          const rPhone = r.phone.replace(/\D/g, '');
          return rPhone.endsWith(cleanPhone) || cleanPhone.endsWith(rPhone);
        });
        if (match) return match;
      }
    }

    return crmRecipients[0]; // fallback
  }, [recipients, crmSelectedPhoneField, crmPhoneFields]);

  /** Map of property key → resolved value (if available). Used for single-record direct insertion. */
  const propertyValues = useMemo(() => {
    const map: Record<string, string> = {};

    // Source 1: conversation metadata (inbox conversations)
    const convProps = conversation.metadata?.contactProperties as Record<string, string> | undefined;
    if (convProps) {
      for (const [k, v] of Object.entries(convProps)) {
        if (v) map[k] = v;
      }
    }

    // Source 2: CRM recipient matching the currently selected phone field
    if (activeCrmRecipient?.properties) {
      for (const [k, v] of Object.entries(activeCrmRecipient.properties)) {
        if (v) map[k] = v;
      }
    }

    // Standard fields
    if (activeCrmRecipient) {
      if (activeCrmRecipient.name) map['name'] = activeCrmRecipient.name;
      if (activeCrmRecipient.phone) map['phone'] = activeCrmRecipient.phone;
    }

    return map;
  }, [conversation.metadata, activeCrmRecipient]);

  /** Whether we're in a single CRM record context — insert values directly, not placeholders */
  const isSingleRecordContext = crmRecordContext && !!activeCrmRecipient;

  const availableProperties = useMemo(() => {
    const keys = Object.keys(propertyValues).filter((k) => {
      // If we have CRM field metadata, only show fields with meaningful data types
      if (meaningfulFieldNames) return meaningfulFieldNames.has(k);
      return true; // No metadata available — show all
    });
    if (keys.length === 0) return [];
    return keys.sort();
  }, [propertyValues, meaningfulFieldNames]);

  const filteredProperties = useMemo(() => {
    // Combine both the # typing filter and the search bar filter
    const combined = (placeholderFilter + placeholderSearch).toLowerCase();
    if (!combined) return availableProperties;
    return availableProperties.filter((p) => {
      // Match against apiName, display label, and value
      const label = fieldDisplayLabels[p] || '';
      const value = propertyValues[p] || '';
      return p.toLowerCase().includes(combined)
        || label.toLowerCase().includes(combined)
        || value.toLowerCase().includes(combined);
    });
  }, [availableProperties, placeholderFilter, placeholderSearch, fieldDisplayLabels, propertyValues]);

  // Text formatting toolbar hook
  const formattingToolbar = useTextSelectionToolbar({
    textareaRef,
    text: message,
    setText: setMessage,
  });

  // Highlight-behind for WhatsApp formatting markers + unresolved placeholders
  const highlightRef = useRef<HTMLDivElement>(null);
  const hasFormatting = /\*[^*\n]+\*|(?<![a-zA-Z0-9])_[^_\n]+_(?![a-zA-Z0-9])|~[^~\n]+~/.test(message);
  const hasUnresolvedPlaceholders = /\$\{[A-Za-z0-9._\-]+\}|\{\{\w+\}\}/.test(message);

  const highlightMarkup = useMemo(() => {
    if (!hasFormatting && !hasUnresolvedPlaceholders) return '';
    let html = message
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // Unresolved placeholders: ${...} and {{...}} → red highlight
    html = html.replace(
      /\$\{[A-Za-z0-9._\-]+\}/g,
      '<span style="color:#ef4444;background:rgba(239,68,68,0.1);border-radius:3px;padding:0 1px">$&</span>'
    );
    html = html.replace(
      /\{\{\w+\}\}/g,
      '<span style="color:#ef4444;background:rgba(239,68,68,0.1);border-radius:3px;padding:0 1px">$&</span>'
    );
    // Bold: *text*
    html = html.replace(
      /\*([^*\n]+)\*/g,
      '<span class="opacity-40">*</span><span class="font-bold">$1</span><span class="opacity-40">*</span>'
    );
    // Italic: _text_
    html = html.replace(
      /(?<![a-zA-Z0-9])_([^_\n]+)_(?![a-zA-Z0-9])/g,
      '<span class="opacity-40">_</span><span class="italic">$1</span><span class="opacity-40">_</span>'
    );
    // Strikethrough: ~text~
    html = html.replace(
      /~([^~\n]+)~/g,
      '<span class="opacity-40">~</span><span class="line-through">$1</span><span class="opacity-40">~</span>'
    );
    return html + '\n';
  }, [message, hasFormatting, hasUnresolvedPlaceholders]);

  // Sync highlight div scroll with textarea scroll
  const syncScroll = useCallback(() => {
    if (textareaRef.current && highlightRef.current) {
      highlightRef.current.scrollTop = textareaRef.current.scrollTop;
      highlightRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, []);

  // Close placeholder dropdown on outside click
  useEffect(() => {
    if (!showPlaceholders) return;
    const handler = (e: MouseEvent) => {
      if (
        placeholderDropdownRef.current &&
        !placeholderDropdownRef.current.contains(e.target as Node) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target as Node)
      ) {
        setShowPlaceholders(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPlaceholders]);

  // WhatsApp 24h window check (ported from original twilio-things.js)
  // Only real customer inbound messages count — notes and system messages are excluded
  const whatsAppWindowExpired = useMemo(() => {
    if (conversation.channel !== 'WHATSAPP') return false;
    const inboundMessages = messages
      .filter((m) => m.direction === 'inbound' && !m.isNote && !m.isSystemMessage)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    if (inboundMessages.length === 0) return true;
    const lastInbound = inboundMessages[0];
    const hoursSince = (Date.now() - new Date(lastInbound.timestamp).getTime()) / (1000 * 60 * 60);
    return hoursSince > 24;
  }, [conversation.channel, messages]);

  // WhatsApp error 131026 — last outbound message failed because the recipient
  // is likely not on WhatsApp (or has blocked the business). Sending another
  // message (or a template) would just fail again, so suppress the 24-hour
  // warning + "Send a template message" affordance and only allow notes.
  const notOnWhatsApp = conversation.channel === 'WHATSAPP'
    && conversation.metadata?.lastMessageStatus === 'failed'
    && (conversation.metadata?.lastFailedCode as number | undefined) === 131026;

  const insertEmoji = useCallback((emoji: string) => {
    const el = textareaRef.current;
    if (!el) {
      setMessage((prev) => prev + emoji);
    } else {
      const start = el.selectionStart;
      const end = el.selectionEnd;
      const newVal = message.substring(0, start) + emoji + message.substring(end);
      setMessage(newVal);
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = start + emoji.length;
        el.focus();
      });
    }
    setShowEmoji(false);
  }, [message]);

  // Insert a selected property as {{property}} placeholder
  const insertPlaceholderProperty = useCallback(
    (property: string) => {
      const el = textareaRef.current;
      if (!el) return;
      const before = message.substring(0, placeholderTriggerIndex);
      const after = message.substring(el.selectionStart);
      // Single CRM record: insert the actual value directly
      // Multi-record / compose: insert {{placeholder}} for per-recipient substitution
      const insertText = isSingleRecordContext && propertyValues[property]
        ? propertyValues[property]
        : `{{${property}}}`;
      const newMsg = before + insertText + after;
      setMessage(newMsg);

      const newPos = placeholderTriggerIndex + insertText.length;
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = newPos;
        el.focus();
      });
      setShowPlaceholders(false);
      setPlaceholderSearch('');
    },
    [message, placeholderTriggerIndex, isSingleRecordContext, propertyValues]
  );

  // Close emoji picker on outside click
  useEffect(() => {
    if (!showEmoji) return;
    const handler = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showEmoji]);

  const handleAttachFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : '';
    setAttachedFile({ file, previewUrl, uploading: false });
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const removeAttachment = useCallback(() => {
    if (attachedFile?.previewUrl) URL.revokeObjectURL(attachedFile.previewUrl);
    setAttachedFile(null);
  }, [attachedFile]);

  // ── Voice Recording ──────────────────────────────────────────
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 } });
      // WhatsApp only supports: audio/aac, audio/amr, audio/mpeg, audio/mp4, audio/ogg (OPUS codecs only, mono)
      const mimeType = MediaRecorder.isTypeSupported('audio/ogg; codecs=opus') ? 'audio/ogg; codecs=opus'
        : MediaRecorder.isTypeSupported('audio/mp4; codecs=aac') ? 'audio/mp4; codecs=aac'
        : MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4'
        : 'audio/webm; codecs=opus'; // fallback
      const recorder = new MediaRecorder(stream, { mimeType });
      recordingChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordingChunksRef.current.push(e.data); };
      // No timeslice — record as single blob so the file header/trailer are valid
      recorder.start();
      mediaRecorderRef.current = recorder;
      setRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => setRecordingDuration((d) => d + 1), 1000);
    } catch (err) {
      console.error('[Voice] Failed to start recording:', err);
    }
  }, []);

  const stopRecording = useCallback(() => {
    return new Promise<File | null>((resolve) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === 'inactive') { resolve(null); return; }
      recorder.onstop = () => {
        const chunks = recordingChunksRef.current;
        if (chunks.length === 0) { resolve(null); return; }
        const ext = recorder.mimeType.includes('ogg') ? 'ogg' : recorder.mimeType.includes('mp4') ? 'm4a' : 'webm';
        const blob = new Blob(chunks, { type: recorder.mimeType });
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: recorder.mimeType });
        resolve(file);
        // Stop all mic tracks
        recorder.stream.getTracks().forEach((t) => t.stop());
      };
      recorder.stop();
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      setRecording(false);
      mediaRecorderRef.current = null;
    });
  }, []);

  const cancelRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      recorder.stream.getTracks().forEach((t) => t.stop());
    }
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    recordingChunksRef.current = [];
    mediaRecorderRef.current = null;
    setRecording(false);
    setRecordingDuration(0);
  }, []);

  const handleSendVoice = useCallback(async () => {
    if (!tpaModule || !sender) return;
    const file = await stopRecording();
    if (!file) return;
    // Permission gate — stop the recording first (so the mic is released) but
    // discard it rather than delivering. This handler is the ONLY gate: no UI
    // control currently starts a recording, so when voice notes are wired up
    // this check must stay (KAN-14).
    if (!canSend) return;
    setSending(true);
    try {
      // Voice notes must also leave from the conversation's own sender.
      const sendSender = (await resolveConversationSender()) ?? sender;
      // Same authoritative re-check as handleSend: the early canSend gate is
      // keyed on the selected sender, delivery happens from this one (KAN-14).
      if (sendSender.id !== sender.id
          && !senderPermissionAllowsSend(getSenderPermissionStandalone(sendSender.id))) {
        toast.error("You don't have permission to send from this conversation's number");
        return;
      }
      // Upload to Firebase Storage first (binary-safe), then CF downloads and re-uploads to Meta
      const uploaded = await uploadAttachment(file);
      const cleanMime = file.type.split(';')[0].trim();

      const waModule = tpaModule as import('@/modules/tpa/whatsapp/whatsapp.module').WhatsAppModule;
      if (typeof waModule.sendVoiceMessage === 'function') {
        const result = await waModule.sendVoiceMessage(
          sendSender.id,
          conversation.contactPhone,
          uploaded.url,
          cleanMime,
          file.name
        );
        // Clean up temp file from Storage after upload
        try { await deleteFile(uploaded.path); } catch { /* ignore */ }
        if (result.success) onMessageSent?.();
      } else {
        // Fallback for non-WhatsApp modules
        const result = await tpaModule.sendMessage({
          from: sendSender,
          to: [{ id: conversation.id, name: conversation.contactName, phone: conversation.contactPhone, source: 'manual' }],
          body: '',
          channel: conversation.channel,
          mediaUrl: uploaded.url,
          mediaType: 'audio',
          mediaFilename: file.name,
        });
        if (result.success) onMessageSent?.();
      }
    } catch (err) {
      console.error('[Voice] Send failed:', err);
    } finally {
      setSending(false);
      setRecordingDuration(0);
      requestAnimationFrame(() => requestAnimationFrame(() => textareaRef.current?.focus()));
    }
  }, [tpaModule, sender, conversation, stopRecording, onMessageSent, resolveConversationSender, canSend]);

  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach((t) => t.stop());
      }
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    };
  }, []);

  const handleSelectTemplate = useCallback((template: MessageTemplate) => {
    setShowTemplateDialog(false);

    // Saved templates (custom/Firebase) → insert body as message text, not as TPA template
    const isSavedTemplate = template.id.startsWith('ua_msg_template_item_');
    if (isSavedTemplate) {
      // Resolve placeholders immediately so user sees actual values in the input
      let resolvedBody = template.body;

      // Resolve ${CURRENT_USER.*} — keep unresolved ones for red highlight
      const user = useAuthStore.getState().user;
      if (user && /\$\{CURRENT_USER\.\w+\}/.test(resolvedBody)) {
        const userFields: Record<string, string> = {
          name: user.displayName || '',
          Full_Name: user.displayName || '',
          email: user.email || '',
          phone: user.phoneNumber || '',
        };
        resolvedBody = resolvedBody.replace(/(\$\{CURRENT_USER\.([A-Za-z0-9_]+)\})/g, (match, _, field) => {
          const val = userFields[field];
          return val != null && val !== '' ? val : match;
        });
      }

      // Resolve ${Module.FieldName} from CRM recipient properties — keep unresolved ones for red highlight
      if (/\$\{[A-Za-z0-9_]+\.\w+\}/.test(resolvedBody)) {
        const crmRecipient = useMessageStore.getState().recipients.find((r) => r.source === 'crm');
        if (crmRecipient?.properties) {
          resolvedBody = resolvedBody.replace(/(\$\{[A-Za-z0-9_]+\.([A-Za-z0-9_]+)\})/g, (match, _, field) => {
            const val = crmRecipient.properties?.[field];
            return val != null && val !== '' ? val : match; // keep placeholder if not found
          });
        }
      }

      // Resolve {{field}} from CRM recipient properties — keep unresolved ones for red highlight
      if (/\{\{\w+\}\}/.test(resolvedBody)) {
        const crmRecipient = useMessageStore.getState().recipients.find((r) => r.source === 'crm');
        if (crmRecipient?.properties) {
          for (const [key, value] of Object.entries(crmRecipient.properties)) {
            if (value) resolvedBody = resolvedBody.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
          }
        }
        if (conversation.contactName) resolvedBody = resolvedBody.replace(/\{\{name\}\}/g, conversation.contactName);
        if (conversation.contactPhone) resolvedBody = resolvedBody.replace(/\{\{phone\}\}/g, conversation.contactPhone);
      }

      setMessage(resolvedBody);
      setSelectedTemplate(null);
      // Auto-resize textarea
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.style.height = 'auto';
          el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
          el.focus();
        }
      });
      return;
    }

    // TPA content template → send as template (with content SID)
    setSelectedTemplate(template);
    setTemplateVariables({});
    // Open variable filling dialog if template has variables or media
    const hasVars = template.variables && template.variables.length > 0;
    const needsMedia = !!template.headerMedia;
    if (hasVars || needsMedia) {
      setShowVariableDialog(true);
    }
  }, []);

  const setTemplateVariable = useCallback((key: string, value: string) => {
    setTemplateVariables((prev) => ({ ...prev, [key]: value }));
  }, []);

  const clearTemplate = useCallback(() => {
    setSelectedTemplate(null);
    setTemplateVariables({});
  }, []);

  const [templateVarError, setTemplateVarError] = useState('');

  const handleSend = async () => {
    if (!tpaModule || !sender) return;
    // The real permission gate. The disabled send button is only a hint — this
    // function is also reached by Enter-to-send, so without this check a
    // 'view_only' user could still deliver a message (KAN-14). Internal notes
    // are exempt: they stay in Firestore and never reach the customer.
    if (!canSend && !isInternalNote()) return;
    // KAN-155: same for an opted-out customer — the locked composer hides the
    // reply box, this stops a send that was already on its way when the STOP
    // arrived. Notes stay allowed.
    if (optOutLocked && !noteMode) return;

    const isTemplateSend = !!selectedTemplate;
    const messageBody = message.trim();

    // Note mode — store in Firestore only, don't send via messaging API.
    // Gated on body OR attachment: an attachment-only note used to fail this
    // check and fall through to the send path below, which delivered the
    // internal file to the customer as a real message.
    const noteFile = attachedFile || useMessageStore.getState().attachedFile;
    if (noteMode && (messageBody || noteFile)) {
      setSending(true);
      setShowMentions(false);
      // NOTE: the composer is cleared further down, after the upload — clearing
      // the text here while the attachment chip stayed until the upload finished
      // made the note land in the thread in two visible steps.
      const fromNum = sender.phoneNumber || sender.id;
      const toNum = conversation.contactPhone;

      // Extract @mentioned emails from note body
      const mentionPattern = /@(\S+)/g;
      const mentionedEmails: string[] = [];
      let match;
      while ((match = mentionPattern.exec(messageBody)) !== null) {
        const mentionName = match[1].toLowerCase();
        // Match mention against org users by name or email prefix
        const matchedUser = Object.values(orgUsers).find(
          (u) => (u.name && u.name.toLowerCase() === mentionName) ||
                 u.email.split('@')[0].toLowerCase() === mentionName
        );
        if (matchedUser && !mentionedEmails.includes(matchedUser.email)) {
          mentionedEmails.push(matchedUser.email);
        }
      }

      // Uploaded to the same `t/` prefix as message media. A note's file is
      // re-read from the note doc on every open, so it would belong in a
      // permanent path (`uploadNoteAttachment`) — but the deployed bucket rules
      // reject unknown prefixes with storage/unauthorized, so that has to wait
      // until `inbox-notes/**` is allowed. Nothing deletes this object: the
      // send flow's deleteFile() only removes the file it uploaded itself.
      let noteAttachment: MessageAttachment | undefined;
      if (noteFile?.file) {
        try {
          const mime = noteFile.file.type || '';
          // Measure the image before uploading. Stored on the attachment, these
          // dimensions let the note bubble reserve the exact box on later loads
          // instead of rendering text first and jumping when the image arrives.
          const dimensions = mime.startsWith('image/')
            ? await readImageDimensions(noteFile.file)
            : null;
          const uploaded = await uploadAttachment(noteFile.file);
          noteAttachment = {
            url: uploaded.url,
            type: mime.startsWith('image/')
              ? 'image'
              : mime.startsWith('video/')
              ? 'video'
              : mime.startsWith('audio/')
              ? 'audio'
              : 'file',
            ...(mime && { contentType: mime }),
            name: noteFile.file.name,
            ...(noteFile.file.size && { size: noteFile.file.size }),
            ...(dimensions && { width: dimensions.width, height: dimensions.height }),
          };
        } catch (err) {
          console.error('[ChatInput] Note attachment upload failed:', err);
          toast.error('Could not upload the attachment — note not added');
          setSending(false);
          return;
        }
      }

      // Empty the composer in one step — text, draft and attachment chip — right
      // before the note is added, so the bubble appears with its media already
      // attached instead of text-first-then-image. Everything above this point
      // is recoverable: an upload failure returns with the composer untouched,
      // so the user can retry without retyping or re-picking the file.
      setMessage('');
      // Text typed on the Reply tab is saved as a draft; switching to the Note
      // tab and sending must clear it too, otherwise the note body sticks around
      // as "Draft: …" in the conversation list and gets refilled into the reply
      // box the next time the chat is opened.
      clearDraft(conversation.id);
      if (attachedFile) setAttachedFile(null);
      if (useMessageStore.getState().attachedFile) useMessageStore.getState().setAttachedFile(null);

      await addNote(conversation.id, messageBody, fromNum, toNum, conversation.channel, mentionedEmails.length > 0 ? mentionedEmails : undefined, noteAttachment);
      setSending(false);
      refocusAfterSend();
      onMessageSent?.();
      return;
    }

    // For non-template sends, require message or attachment
    if (!isTemplateSend && !messageBody && !attachedFile) return;

    // Validate template variables are filled
    if (isTemplateSend && selectedTemplate?.variables && selectedTemplate.variables.length > 0) {
      const missing = selectedTemplate.variables.filter((v) => !templateVariables[v]?.trim());
      if (missing.length > 0) {
        setTemplateVarError(`Fill in: ${missing.map((v) => `{{${v}}}`).join(', ')}`);
        return;
      }
    }
    setTemplateVarError('');

    setSending(true);

    // Make sure the reply goes out from the conversation's own sender — not
    // whatever default/test sender happened to be selected (see
    // resolveConversationSender above).
    const sendSender = (await resolveConversationSender()) ?? sender;

    // Authoritative permission gate — keyed on the sender that will actually
    // deliver. The early canSend check above uses the composer's selected
    // sender, which can be a different number than the conversation's own
    // (resolved here), so re-check before anything leaves (KAN-14).
    if (sendSender.id !== sender.id
        && !senderPermissionAllowsSend(getSenderPermissionStandalone(sendSender.id))) {
      toast.error("You don't have permission to send from this conversation's number");
      setSending(false);
      return;
    }

    // Message Signature (Settings › Compose View) applies to every outbound
    // message, matching the compose view. Notes return above, so they never
    // get one. Approved TPA templates are excluded because the provider drops
    // Body in favour of the ContentSid payload — appending there would not
    // reach the recipient but would still pollute the CRM history record.
    const signature = useSettingsStore.getState().preferences.messageSignature?.trim();
    // KAN-155: then the opt-out footer, when Settings › Customer Opt-out adds it to inbox replies.
    const optOutSettingsNow = useSettingsStore.getState().optOutSettings;
    const withSignature = (text: string) =>
      appendOptOutFooter(signature && text ? `${text}\n${signature}` : text, optOutSettingsNow, 'inbox', conversation.channel);

    const optimisticId = `optimistic_${Date.now()}`;
    // Resolve entity placeholders in variable values for the optimistic preview too
    const previewCrmRecipient = useMessageStore.getState().recipients.find((r) => r.source === 'crm');
    const previewVars = isTemplateSend && Object.keys(templateVariables).length > 0
      ? resolveTemplateVariables(templateVariables, {
          name: conversation.contactName || undefined,
          phone: conversation.contactPhone || undefined,
          email: previewCrmRecipient?.email,
          properties: previewCrmRecipient?.properties,
        })
      : templateVariables;

    const optimisticBody = isTemplateSend
      ? (() => {
          // Resolve template body by replacing {{key}} placeholders with variable values
          let resolved = selectedTemplate?.body || `[Template] ${selectedTemplate?.name || ''}`;
          if (previewVars) {
            for (const [key, value] of Object.entries(previewVars)) {
              if (!key.startsWith('URL_')) {
                resolved = resolved.replaceAll(`{{${key}}}`, value);
              }
            }
          }
          return resolved;
        })()
      : withSignature(messageBody);

    const isScheduledSend = !!scheduledAt;

    // If there's an attached file, populate the optimistic message with the local
    // preview URL so the chat bubble renders the attachment immediately instead of
    // showing "Empty message" while the file uploads in the background.
    // Check BOTH local state (clip icon upload) and message store (template dialog upload).
    const optimisticFile = attachedFile || useMessageStore.getState().attachedFile;
    // The message-store entry may carry no File (template-dialog upload) — mime defaults to ''.
    const optimisticMime = optimisticFile?.file?.type || '';
    const optimisticAttachments = optimisticFile
      ? [{
          url: optimisticFile.previewUrl || '',   // local blob URL for images; empty for non-images
          // Same image/video/audio/file split the refetched provider message
          // gets, so the bubble doesn't flip from the generic file card to the
          // audio player / media link card a few seconds after sending.
          type: optimisticMime.startsWith('image/')
            ? 'image' as const
            : optimisticMime.startsWith('video/')
            ? 'video' as const
            : optimisticMime.startsWith('audio/')
            ? 'audio' as const
            : 'file' as const,
          contentType: optimisticMime || undefined,
        }]
      : undefined;

    const optimisticMsg: Message = {
      id: optimisticId,
      // Stable React-list identity — survives the id→SID swap below and
      // every reconciliation update, so the row never remounts/jumps.
      clientId: optimisticId,
      conversationId: conversation.id,
      from: sendSender.phoneNumber || sendSender.id,
      to: conversation.contactPhone,
      body: optimisticBody,
      channel: conversation.channel,
      direction: 'outbound',
      status: isScheduledSend ? 'scheduled' : 'queued',
      timestamp: new Date(),
      ...(optimisticAttachments && { attachments: optimisticAttachments }),
      ...(isScheduledSend && { metadata: { scheduledAt: scheduledAt.toISOString() } }),
      ...(isTemplateSend && !isScheduledSend && { metadata: { isTemplate: true } }),
      ...(replyingTo && { replyToMessageId: replyingTo.id }),
      // ConvertedMessage fields for immediate rendering
      ...(isTemplateSend && selectedTemplate?.buttons && {
        buttons: selectedTemplate.buttons.map(b => ({ text: b.text, type: b.type, url: b.url })),
      }),
      ...(currentUser && {
        author: {
          name: currentUser.displayName || currentUser.email,
          email: currentUser.email,
          pic: currentUser.photoURL,
          uid: currentUser.uid,
          type: 'uaapp',
        },
      }),
    } as Message;
    addMessage(optimisticMsg);
    // Optimistically update conversation list subject + status immediately
    const store = useConversationStore.getState();
    store.prependOrUpdateConversation(conversation.id, optimisticBody, new Date(), false);
    store.updateConversation(conversation.id, {
      metadata: {
        ...(conversation.metadata || {}),
        lastMessageStatus: isScheduledSend ? 'scheduled' : 'queued',
        lastMessageDirection: 'outbound',
        ...(isTemplateSend && { lastMessageType: 'template' }),
        ...(!isTemplateSend && { lastMessageType: undefined }),
      },
    } as Partial<Record<string, unknown>>);
    setMessage('');
    clearDraft(conversation.id);
    // The template variable dialog stores the uploaded file in the MESSAGE STORE,
    // but this component has its own local attachedFile state for direct file attachment.
    // Check both: prefer local (direct attachment via clip icon), fall back to message store
    // (file uploaded via template variable dialog's media input).
    const messageStoreFile = useMessageStore.getState().attachedFile;
    const savedFile = attachedFile || (messageStoreFile ? { file: messageStoreFile.file, previewUrl: messageStoreFile.previewUrl, uploading: false } : null);
    // Clear attached file state — but DON'T revoke the blob URL yet because the
    // optimistic message is still using it to render the image preview.
    // We'll revoke it later after the Firebase upload URL replaces the blob URL.
    const blobUrlToRevokeLater = savedFile?.previewUrl;
    // Bypass the store's revokeObjectURL by setting a dummy previewUrl before clearing
    if (messageStoreFile && blobUrlToRevokeLater) {
      // Temporarily replace previewUrl so the store setter doesn't revoke our blob
      useMessageStore.setState({ attachedFile: { ...messageStoreFile, previewUrl: '' } });
    }
    setAttachedFile(null);
    if (messageStoreFile) useMessageStore.getState().setAttachedFile(null);
    const savedTemplate = selectedTemplate;
    const rawVars = { ...templateVariables };

    // Resolve {{property}} entity placeholders inside template variable values
    // (e.g. {{First_Name}} → "John") using CRM contact data.
    // This mirrors original app's getTemplateAppliedMessage() on each placeholder value.
    const crmRecipient = useMessageStore.getState().recipients.find((r) => r.source === 'crm');
    const savedVars = isTemplateSend && Object.keys(rawVars).length > 0
      ? resolveTemplateVariables(rawVars, {
          name: conversation.contactName || undefined,
          phone: conversation.contactPhone || undefined,
          email: crmRecipient?.email,
          properties: crmRecipient?.properties,
        })
      : rawVars;

    clearTemplate();

    try {
      // Upload file to Firebase Storage temp path to get a public URL
      let mediaUrl = savedFile?.mediaUrl;
      let uploadedPath: string | undefined;
      if (savedFile?.file && !mediaUrl) {
        console.log('[ChatInput] Uploading attached file to Firebase:', savedFile.file.name, savedFile.file.size, 'bytes');
        const uploaded = await uploadAttachment(savedFile.file);
        mediaUrl = uploaded.url;
        uploadedPath = uploaded.path;
        console.log('[ChatInput] File uploaded:', mediaUrl?.substring(0, 80));

        // Replace the optimistic message's blob URL with the real Firebase Storage URL
        // so the image continues to display after the blob is revoked.
        if (mediaUrl) {
          const uploadStore = useConversationStore.getState();
          useConversationStore.setState({
            messages: uploadStore.messages.map((m) =>
              m.id === optimisticId && m.attachments
                ? { ...m, attachments: m.attachments.map((a) => ({ ...a, url: mediaUrl! })) }
                : m
            ),
          });
          // Now safe to revoke the blob URL
          if (blobUrlToRevokeLater) {
            try { URL.revokeObjectURL(blobUrlToRevokeLater); } catch { /* ignore */ }
          }
        }
      }

      // Resolve ${CURRENT_USER.*} placeholders
      let finalBody = isTemplateSend ? '' : messageBody;
      if (!isTemplateSend && /\$\{CURRENT_USER\.\w+\}/.test(finalBody)) {
        const user = useAuthStore.getState().user;
        if (user) {
          const userFields: Record<string, string> = {
            name: user.displayName || '',
            Full_Name: user.displayName || '',
            email: user.email || '',
            phone: user.phoneNumber || '',
          };
          finalBody = finalBody.replace(/\$\{CURRENT_USER\.([A-Za-z0-9_]+)\}/g, (_, field) => userFields[field] || '');
        }
      }
      // Resolve ${Module.FieldName} placeholders from CRM recipient properties
      if (!isTemplateSend && /\$\{[A-Za-z0-9_]+\.\w+\}/.test(finalBody)) {
        const crmRecipient = useMessageStore.getState().recipients.find((r) => r.source === 'crm');
        if (crmRecipient?.properties) {
          finalBody = finalBody.replace(/\$\{[A-Za-z0-9_]+\.([A-Za-z0-9_]+)\}/g, (_, field) => crmRecipient.properties?.[field] ?? '');
        }
        finalBody = finalBody.replace(/\$\{[A-Za-z0-9._\-]+\}/g, '');
      }
      // Substitute {{key}} placeholders from CRM recipient properties
      if (!isTemplateSend && /\{\{\w+\}\}/.test(finalBody)) {
        const crmRecipient = useMessageStore.getState().recipients.find((r) => r.source === 'crm');
        if (crmRecipient?.properties) {
          for (const [key, value] of Object.entries(crmRecipient.properties)) {
            if (value) {
              finalBody = finalBody.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
            }
          }
        }
        // Standard field fallbacks
        finalBody = finalBody.replace(/\{\{name\}\}/g, conversation.contactName || '');
        finalBody = finalBody.replace(/\{\{phone\}\}/g, conversation.contactPhone || '');
        // Clear any remaining unresolved placeholders
        finalBody = finalBody.replace(/\{\{\w+\}\}/g, '');
      }

      // Append after placeholder resolution so the signature is never itself
      // treated as a placeholder, and so every downstream consumer of
      // finalBody (provider payload, sent-message meta, CRM history, inbox
      // record) carries the same text. No-op for templates, whose finalBody is ''.
      finalBody = withSignature(finalBody);

      const savedScheduledAt = scheduledAt;
      setScheduledAt(null);

      const savedReplyToId = replyingTo?.id;

      // Derive WhatsApp media type from MIME type so images/videos aren't sent as documents
      let mediaType: 'image' | 'video' | 'audio' | 'document' | undefined;
      let mediaFilename: string | undefined;
      if (mediaUrl && savedFile?.file) {
        const mime = savedFile.file.type || '';
        if (mime.startsWith('image/')) mediaType = 'image';
        else if (mime.startsWith('video/')) mediaType = 'video';
        else if (mime.startsWith('audio/')) mediaType = 'audio';
        else mediaType = 'document';
        mediaFilename = savedFile.file.name || undefined;
      }

      const result = await tpaModule.sendMessage({
        from: sendSender,
        to: [
          {
            id: conversation.id,
            name: conversation.contactName,
            phone: conversation.contactPhone,
            source: 'manual',
          },
        ],
        body: finalBody,
        channel: conversation.channel,
        mediaUrl,
        mediaType,
        mediaFilename,
        templateId: savedTemplate?.id,
        selectedTemplate: savedTemplate ?? undefined,
        templateVariables: Object.keys(savedVars).length > 0 ? savedVars : undefined,
        scheduledAt: savedScheduledAt ?? undefined,
        replyToMessageId: savedReplyToId ?? undefined,
      });

      // Clean up temp file from storage after Twilio has fetched it
      // Only for Twilio — other TPAs may need the URL to remain accessible
      if (uploadedPath && useAppStore.getState().appCodeTPA === 'twilio') {
        deleteFile(uploadedPath).catch(() => {});
      }

      if (result.success) {
        // Clear reply state after successful send
        onCancelReply?.();
        const successStatus = isScheduledSend ? 'scheduled' as const : 'sent' as const;
        const successStore = useConversationStore.getState();
        useConversationStore.setState({
          messages: successStore.messages.map((m) =>
            m.id === optimisticId
              ? { ...m, id: result.messageId || m.id, status: successStatus }
              : m
          ),
        });

        // Store sent message meta (source/author, resolved text) so shared
        // users see proper author attribution. No-op for WhatsApp.
        if (result.messageId && conversation.contactPhone) {
          successStore.storeSentMessageMeta(
            conversation.id,
            toContactId(conversation.contactPhone),
            result.messageId,
            finalBody || messageBody
          ).catch(() => {});
        }

        // Auto-assign unassigned conversations to the current user on send.
        // "Unassigned" = no assigneeEmail OR literal 'unassigned' string.
        // Skip if the chat is already assigned (including to someone else —
        // taking it over should be an explicit action).
        const currentAssignee = conversation.assigneeEmail;
        const isUnassigned = !currentAssignee || currentAssignee === 'unassigned';
        if (isUnassigned && currentUser?.email) {
          successStore.autoAssignIfUnassigned(conversation.id, currentUser.email).catch(() => {});
        }
        // Update conversation status
        successStore.updateConversation(conversation.id, {
          metadata: {
            ...((successStore.conversations.find(c => c.id === conversation.id)?.metadata) || {}),
            lastMessageStatus: successStatus,
            lastMessageDirection: 'outbound',
          },
        } as Partial<Record<string, unknown>>);

        // Log sent message to CRM history (non-blocking)
        // Uses tpaModule.logSentMessageToHistory which resolves {{1}}, {{2}} template
        // placeholders from selectedTemplate.body + templateVariables before logging.
        //
        // Resolution order for the CRM record to attach the note to:
        //   1. An explicit CRM recipient in the message-store (set when the app
        //      booted inside a CRM widget context and `addRecordsAsRecipients`
        //      populated recipients).
        //   2. Fallback: `conversation.crmRecord` — populated during incoming
        //      sync when the contact was matched against a CRM record. This
        //      lets the standalone Inbox view also write notes to the linked
        //      CRM record without requiring a CRM widget context.
        try {
          const crmR = recipients.find((r) => r.source === 'crm');
          // Only trust the conversation's crmRecord if its appCode matches
          // the currently active extension — a conversation persisted under
          // a different SaaS (e.g. switched apps) shouldn't post notes here.
          const currentExt = useAppStore.getState().extensionName;
          const linkedCrmRecord =
            !crmR && conversation.crmRecord
              && (!conversation.crmRecord.appCode || conversation.crmRecord.appCode === currentExt)
              ? conversation.crmRecord
              : null;

          const replyPayloadForLogging = {
            from: sendSender,
            to: [
              {
                id: conversation.id,
                name: conversation.contactName,
                phone: conversation.contactPhone,
                source: 'manual' as const,
                module: crmR?.module ?? linkedCrmRecord?.module,
                entityId: crmR?.entityId ?? linkedCrmRecord?.entityId,
              },
            ],
            body: finalBody || messageBody,
            channel: conversation.channel,
            templateId: savedTemplate?.id,
            selectedTemplate: savedTemplate ?? undefined,
            templateVariables: Object.keys(savedVars).length > 0 ? savedVars : undefined,
            mediaUrl,
          };
          tpaModule.logSentMessageToHistory?.(replyPayloadForLogging, result).catch(() => {});
          // Seed UA Inbox with author info — no-op for MessageBird and
          // WhatsApp (which writes inline). See base-tpa-module.ts for
          // the conv-ID format.
          tpaModule.logOutgoingMessageToInbox?.(replyPayloadForLogging, result).catch(() => {});
          tpaModule.updateConversationLinkedRecord?.(replyPayloadForLogging, result).catch(() => {});
        } catch {
          // Non-fatal
        }

        // Mark conversation as read after sending — agent has seen all messages.
        // Server derives the read index from the conv doc's `messagesCount`.
        if (conversation.contactPhone) {
          markMessageAsReadInConversation(
            conversation.id,
            toContactId(conversation.contactPhone),
          ).catch(() => {});
        }

        // Real-time RTDB listener handles subsequent status updates (sent → delivered → read)

        // Single-recipient CRM history is already logged above via
        // tpaModule.logSentMessageToHistory (which delegates to
        // saasModule.addSentMessageToHistory). A second direct call here
        // would create duplicate notes/timeline entries in CRMs like Pipedrive.
      } else {
        const store = useConversationStore.getState();
        useConversationStore.setState({
          messages: store.messages.map((m) =>
            m.id === optimisticId
              ? { ...m, status: 'failed' as const, metadata: { ...m.metadata, errorMessage: result.error } }
              : m
          ),
        });
      }

      onMessageSent?.();
    } catch {
      const store = useConversationStore.getState();
      useConversationStore.setState({
        messages: store.messages.map((m) =>
          m.id === optimisticId
            ? { ...m, status: 'failed' as const }
            : m
        ),
      });
    } finally {
      setSending(false);
      refocusAfterSend();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // 1. Handle # placeholder dropdown navigation first
    if (showPlaceholders && filteredProperties.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedPropIndex((i) => Math.min(i + 1, filteredProperties.length - 1));
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedPropIndex((i) => Math.max(i - 1, 0));
        return;
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertPlaceholderProperty(filteredProperties[selectedPropIndex]);
        return;
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowPlaceholders(false);
        setPlaceholderSearch('');
        return;
      }
    }

    // 2. Handle @ mention dropdown navigation
    if (showMentions && filteredMentionUsers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedMentionIndex((i) => Math.min(i + 1, filteredMentionUsers.length - 1));
        return;
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedMentionIndex((i) => Math.max(i - 1, 0));
        return;
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const user = filteredMentionUsers[selectedMentionIndex];
        if (user) insertMention(user.email, user.name);
        return;
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowMentions(false);
        return;
      }
    }

    // 3. Enter-to-send (Settings › Chat View › Input). When the setting is on,
    //    plain Enter sends and Shift+Enter breaks the line; when off the two
    //    swap. Whichever key isn't "send" is left to the textarea's default
    //    behaviour (newline).
    if (e.key === 'Enter') {
      const isSendKey = enterToSend ? !e.shiftKey : e.shiftKey;
      if (isSendKey) {
        e.preventDefault();
        // handleSend() carries the permission gate (and the internal-note
        // exemption), so Enter routes through the same check as the button.
        handleSend();
      }
    }
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    const el = e.target;
    setMessage(val);
    // Only real keystrokes count as "typing" — draft restore, emoji picker and
    // template fills set `message` too but are not a live collision signal.
    // Internal notes never reach the customer, so they are not a collision.
    onTypingChange?.(!noteMode && val.trim().length > 0);
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;

    // Save draft (only for normal messages, not notes)
    if (!noteMode) {
      saveDraft(conversation.id, val);
    }

    // Detect / at start of message → open template dialog
    // For WhatsApp channels, allow / even if templates aren't cached in IDB yet —
    // openTemplateDialog() calls refreshTemplates() which fetches from API on open.
    // For SMS channels, only trigger if templates are already cached (existing behavior).
    // Never in note mode — the note branch can't send a template, so the picker
    // would swallow the "/" and leave the user with an unusable selection.
    if (val === '/' && !noteMode && (templates.length > 0 || isWhatsApp)) {
      setMessage('');
      el.style.height = 'auto';
      openTemplateDialog();
      return;
    }

    // Detect # placeholder trigger
    if (availableProperties.length > 0) {
      const cursorPos = el.selectionStart;
      const textBeforeCursor = val.substring(0, cursorPos);
      const hashIndex = textBeforeCursor.lastIndexOf('#');

      if (hashIndex >= 0) {
        const textAfterHash = textBeforeCursor.substring(hashIndex + 1);
        if (!textAfterHash.includes(' ') && !textAfterHash.includes('\n')) {
          setPlaceholderFilter(textAfterHash);
          setPlaceholderTriggerIndex(hashIndex);
          setShowPlaceholders(true);
          setPlaceholderSearch('');
          setSelectedPropIndex(0);
          return;
        }
      }
    }
    setShowPlaceholders(false);
    setPlaceholderSearch('');

    // Detect @ mention trigger (only in note mode)
    if (noteMode) {
      const cursorPos = el.selectionStart;
      const textBeforeCursor = val.substring(0, cursorPos);
      const atIndex = textBeforeCursor.lastIndexOf('@');

      if (atIndex >= 0) {
        // Only trigger if @ is at start or preceded by a space/newline
        const charBefore = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';
        if (charBefore === ' ' || charBefore === '\n' || atIndex === 0) {
          const textAfterAt = textBeforeCursor.substring(atIndex + 1);
          if (!textAfterAt.includes(' ') && !textAfterAt.includes('\n')) {
            setMentionFilter(textAfterAt);
            setMentionTriggerIndex(atIndex);
            setShowMentions(true);
            setSelectedMentionIndex(0);
            return;
          }
        }
      }
      setShowMentions(false);
    } else {
      setShowMentions(false);
    }
  };

  const isDisabled = !sender || sending;

  /**
   * Restore focus to the textarea after a send completes. The textarea is
   * `disabled` while `sending=true`, and a disabled element silently
   * refuses `.focus()` — so calling focus() inside the same tick as
   * `setSending(false)` no-ops because React hasn't re-rendered yet.
   * Defer to the next animation frame so the textarea is enabled first.
   */
  const refocusAfterSend = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    });
  }, []);
  const hasTemplateVars = selectedTemplate?.variables && selectedTemplate.variables.length > 0;
  const hasUnfilledVars = hasTemplateVars && selectedTemplate!.variables!.some((v) => !templateVariables[v]?.trim());

  // Presence: any path that empties the box (send, clear, tab switch) or
  // switches to Note mode means I stopped drafting a reply. Keystrokes flip
  // it back on in handleInput.
  useEffect(() => {
    if (noteMode || message.trim().length === 0) onTypingChange?.(false);
  }, [message, noteMode, onTypingChange]);

  const othersTypingLabel = presenceOthers ? typingLabel(presenceOthers) : null;
  const presenceInline = !!showPresenceInline && !!presenceOthers && presenceOthers.length > 0;
  const presenceStripVisible = !!othersTypingLabel || presenceInline;
  // The Reply/Note pill floats ABOVE the composer border (absolute, -top). It
  // is hidden when a reply preview or attachment sits there, but the presence
  // strip must stay visible while a colleague types — so when both are on
  // screen the composer gets extra top padding for the pill to float into.
  const showReplyNoteToggle = !!sender && !selectedTemplate && !replyingTo && !attachedFile;

  return (
    <div className="shrink-0">
      {/* User collision. Amber when a colleague is drafting a reply in this
          same chat; neutral "also here" row when the header (and its avatars)
          is hidden. Warn only — never block sending. */}
      {(othersTypingLabel || presenceInline) && (
        <div
          data-testid="chat-presence-strip"
          className={cn(
            'flex items-center gap-2 px-3 py-1.5 border-b text-xs',
            othersTypingLabel
              ? 'bg-amber-50 dark:bg-amber-950/50 border-amber-200 dark:border-amber-800/50 text-amber-700 dark:text-amber-400'
              : 'bg-muted/30 border-border text-muted-foreground',
          )}
        >
          {/* Chips say who + what ("Sabariraja viewing"); the amber sentence
              is the explicit collision warning when someone is replying. */}
          {presenceInline && <ChatPresenceAvatars users={presenceOthers!} />}
          {othersTypingLabel && (
            <>
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
              <span data-testid="chat-presence-typing" className="truncate">{othersTypingLabel}</span>
            </>
          )}
        </div>
      )}

      {/* WhatsApp 24h window warning — suppressed when the recipient looks
          like they're not on WhatsApp (131026); the chat-thread already shows
          a more accurate banner above and a template send would just fail. */}
      {whatsAppWindowExpired && !notOnWhatsApp && (
        <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/50 border-b border-amber-200 dark:border-amber-800/50 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          <span>
            {messages.length === 0
              ? 'You have to send a template message first to start WhatsApp conversation.'
              : '24-hour WhatsApp window has expired. Only template messages can be sent.'}
          </span>
        </div>
      )}

      {/* Reply-to preview bar */}
      {replyingTo && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/30">
          <div className="flex-1 min-w-0 border-l-2 border-primary pl-2">
            <p className="text-[11px] font-semibold text-primary leading-tight">
              {replyingTo.direction === 'outbound' ? 'You' : (replyingTo.from?.replace('whatsapp:', '') || conversation.contactName || 'Contact')}
            </p>
            <p className="text-xs text-muted-foreground truncate leading-tight mt-0.5">
              {replyingTo.attachments?.length ? '📎 Media' : ''}
              {replyingTo.attachments?.length && replyingTo.body ? ' · ' : ''}
              {replyingTo.body
                ? (replyingTo.body.length > 80 ? replyingTo.body.slice(0, 80) + '…' : replyingTo.body)
                : (replyingTo.attachments?.length ? '' : 'Message')}
            </p>
          </div>
          <button
            onClick={onCancelReply}
            className="shrink-0 rounded-full p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            title="Cancel reply"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Selected template preview — compact card matching compose view */}
      {selectedTemplate && (
        <div className="px-3 py-2 border-b border-border">
          <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2 min-w-0">
                <FileText className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-primary truncate">
                    {selectedTemplate.name}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                    {selectedTemplate.body}
                  </p>
                  {hasTemplateVars && (
                    <div className="flex items-center gap-2 mt-2">
                      <span className={cn('text-[10px] flex items-center gap-1', !hasUnfilledVars ? 'text-green-600' : 'text-muted-foreground')}>
                        {!hasUnfilledVars && <Check className="h-2.5 w-2.5" />}
                        {selectedTemplate.variables!.filter((v) => templateVariables[v]?.trim()).length}/{selectedTemplate.variables!.length} variables filled
                      </span>
                      <button
                        onClick={() => setShowVariableDialog(true)}
                        className="text-[10px] text-primary hover:text-primary/80 flex items-center gap-0.5 transition-colors"
                      >
                        <Pencil className="h-2.5 w-2.5" />
                        Edit
                      </button>
                    </div>
                  )}
                  {templateVarError && (
                    <p className="text-[10px] text-destructive flex items-center gap-1 mt-1">
                      <AlertTriangle className="h-3 w-3 shrink-0" />
                      {templateVarError}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => openTemplateDialog()}
                  className="rounded p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  title="Change template"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
                <button
                  onClick={clearTemplate}
                  className="rounded p-1 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                  title="Remove template"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* File attachment preview */}
      {attachedFile && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
          {attachedFile.previewUrl ? (
            <img
              src={attachedFile.previewUrl}
              alt="Attachment preview"
              className="h-12 w-12 rounded-lg object-cover"
            />
          ) : (
            <div className="h-12 w-12 rounded-lg bg-muted flex items-center justify-center">
              <ImageIcon className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-foreground truncate">
              {attachedFile.file.name}
            </p>
            <p className="text-[10px] text-muted-foreground">
              {(attachedFile.file.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <button
            onClick={removeAttachment}
            className="shrink-0 rounded-full p-1 hover:bg-muted transition-colors"
          >
            <X className="h-3.5 w-3.5 text-muted-foreground" />
          </button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        data-testid="chat-file-input"
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
        className="hidden"
        onChange={handleFileSelected}
      />

      <div className={cn('p-3 relative', presenceStripVisible && showReplyNoteToggle && 'pt-10')}>
        {!sender ? (
          <div className="text-center py-2">
            <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Loading sender...</span>
            </div>
          </div>
        ) : activeFlow && !noteMode ? (
          /* Chat Automation is driving this conversation — composer is locked
             until the agent stops the flow. Flow name + current stage shown so
             the agent knows what's running. Internal notes remain available. */
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 text-xs text-muted-foreground min-w-0">
              <Workflow className="h-4 w-4 shrink-0 text-violet-500" />
              <span className="truncate">
                In flow <span className="font-medium text-foreground">{activeFlow.flowName}</span>
                {activeFlow.stage ? <span className="text-muted-foreground"> · {activeFlow.stage}</span> : null}
              </span>
            </div>
            <button
              type="button"
              onClick={handleStopFlow}
              disabled={stoppingFlow}
              className="flex items-center gap-1.5 rounded-lg border border-rose-300/50 bg-rose-500/5 px-3 py-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 transition-colors disabled:opacity-60"
            >
              {stoppingFlow ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <StopCircle className="h-3.5 w-3.5" />}
              <span>Stop flow</span>
            </button>
            {!isPlaceholder && (
              <button
                type="button"
                onClick={() => { setNoteMode(true); setSelectedTemplate(null); }}
                className="flex items-center gap-1.5 rounded-lg border border-amber-300/50 bg-amber-500/5 px-3 py-1.5 text-xs font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 transition-colors"
              >
                <StickyNote className="h-3.5 w-3.5" />
                <span>Add Note</span>
              </button>
            )}
          </div>
        ) : optOutLocked && !noteMode ? (
          /* KAN-155: the customer opted out — composer locked until the agent
             confirms "Send anyway" (this chat only). Notes stay available. */
          <div className="space-y-2">
          {confirmSendAnyway && isOptOutSmsChannel(conversation.channel) && (
            <OptOutSmsWarning text={OPT_OUT_SMS_WARNINGS.sendAnyway} testId="inbox-optout-sms-warning" />
          )}
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 text-xs text-muted-foreground min-w-0">
              <Ban className="h-4 w-4 shrink-0 text-rose-500" />
              <span className="truncate">
                {confirmSendAnyway ? (
                  <>
                    This customer asked not to be messaged.{' '}
                    <span className="font-medium text-foreground">Send anyway?</span>
                  </>
                ) : (
                  <>
                    <span className="font-medium text-foreground">Customer opted out</span>
                    {conversation.optOut?.keyword ? ` · replied ${conversation.optOut.keyword}` : ''}
                    {conversation.optOut?.time
                      ? ` on ${new Date(conversation.optOut.time).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`
                      : ''}
                  </>
                )}
              </span>
            </div>
            {confirmSendAnyway ? (
              <>
                <button
                  type="button"
                  onClick={() => { onOptOutSendAnyway(); setConfirmSendAnyway(false); }}
                  className="flex items-center gap-1.5 rounded-lg border border-rose-300/50 bg-rose-500/10 px-3 py-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-500/20 transition-colors"
                >
                  <span>Yes, send anyway</span>
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmSendAnyway(false)}
                  className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
                >
                  <span>Cancel</span>
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setConfirmSendAnyway(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-rose-300/50 bg-rose-500/5 px-3 py-1.5 text-xs font-medium text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 transition-colors"
                >
                  <Send className="h-3.5 w-3.5" />
                  <span>Send anyway</span>
                </button>
                {!isPlaceholder && (
                  <button
                    type="button"
                    onClick={() => { setNoteMode(true); setSelectedTemplate(null); }}
                    className="flex items-center gap-1.5 rounded-lg border border-amber-300/50 bg-amber-500/5 px-3 py-1.5 text-xs font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 transition-colors"
                  >
                    <StickyNote className="h-3.5 w-3.5" />
                    <span>Add Note</span>
                  </button>
                )}
              </>
            )}
          </div>
          </div>
        ) : assignedToOther && !noteMode ? (
          /* Chat is assigned to another agent — show assign-to-me + add-note options */
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 text-xs text-muted-foreground">
              <UserCheck className="h-4 w-4 shrink-0 text-amber-500" />
              <span>
                Assigned to <span className="font-medium text-foreground">{conversation.assigneeEmail?.split('@')[0]}</span>
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                if (currentUser?.email) {
                  updateConvAssignee(conversation.id, currentUser.email);
                }
              }}
              className="flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
            >
              <UserCheck className="h-3.5 w-3.5" />
              <span>Assign to Me</span>
            </button>
            {!isPlaceholder && (
              <button
                type="button"
                onClick={() => { setNoteMode(true); setSelectedTemplate(null); }}
                className="flex items-center gap-1.5 rounded-lg border border-amber-300/50 bg-amber-500/5 px-3 py-1.5 text-xs font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 transition-colors"
              >
                <StickyNote className="h-3.5 w-3.5" />
                <span>Add Note</span>
              </button>
            )}
          </div>
        ) : notOnWhatsApp && !noteMode ? (
          /* Recipient appears not to be on WhatsApp (error 131026) — only
             allow adding internal notes. No template option since it would
             just fail again with the same error. */
          <div className={cn("flex gap-2", isMini ? "items-center" : "items-center gap-3")}>
            <div className="flex items-center gap-2 flex-1">
              {!isPlaceholder && activeView === 'CHAT' && (
                <ConvAssigneePicker
                  conversationId={conversation.id}
                  currentAssignee={conversation.assigneeEmail}
                  compact
                  botActive={!!conversation.activeFlow}
                  botLabel={conversation.activeFlow?.flowName}
                />
              )}
              <button
                type="button"
                onClick={() => { setNoteMode(true); setSelectedTemplate(null); }}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 rounded-xl border border-dashed border-amber-300/60 bg-amber-500/5 font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 hover:border-amber-400/70 transition-colors",
                  isMini ? "px-3 py-2 text-xs" : "px-4 py-2.5 text-sm"
                )}
              >
                <StickyNote className={isMini ? "h-3.5 w-3.5" : "h-4 w-4"} />
                <span>Add an internal note</span>
              </button>
            </div>
          </div>
        ) : whatsAppWindowExpired && !selectedTemplate && !noteMode ? (
          /* Clean template-only UI when WhatsApp 24h window expired */
          <div className={cn("flex gap-2", isMini ? "items-center" : "items-center gap-3")}>
            <div className="flex items-center gap-2 flex-1">
              {/* Assignee avatar — only in CHAT view */}
              {!isPlaceholder && activeView === 'CHAT' && (
                <ConvAssigneePicker
                  conversationId={conversation.id}
                  currentAssignee={conversation.assigneeEmail}
                  compact
                  botActive={!!conversation.activeFlow}
                  botLabel={conversation.activeFlow?.flowName}
                />
              )}
              <button
                type="button"
                onClick={() => openTemplateDialog()}
                disabled={templatesLoading}
                className={cn(
                  "flex-1 flex items-center justify-center gap-2 rounded-xl border border-dashed border-primary/40 bg-primary/5 font-medium text-primary hover:bg-primary/10 hover:border-primary/60 transition-colors",
                  isMini ? "px-3 py-2 text-xs" : "px-4 py-2.5 text-sm"
                )}
              >
                <FileText className={isMini ? "h-3.5 w-3.5" : "h-4 w-4"} />
                <span>Send a template message</span>
              </button>
            </div>
            {!isPlaceholder && (
              isMini ? (
                <button
                  type="button"
                  onClick={() => { setNoteMode(true); setSelectedTemplate(null); }}
                  className="shrink-0 rounded-lg p-2 bg-amber-500/10 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20 transition-colors"
                  title="Add Note"
                >
                  <StickyNote className="h-3.5 w-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => { setNoteMode(true); setSelectedTemplate(null); }}
                  className="shrink-0 flex items-center justify-center gap-1.5 rounded-xl border border-amber-300/50 bg-amber-500/5 font-medium text-amber-600 dark:text-amber-400 hover:bg-amber-500/10 transition-colors px-3 py-2.5 text-sm"
                >
                  <StickyNote className="h-4 w-4" />
                  <span>Add Note</span>
                </button>
              )
            )}
          </div>
        ) : (
          <div className={cn(
            "relative rounded-xl transition-all",
            isMini
              ? 'border-0'
              : 'border border-border/60 focus-within:border-primary/30 focus-within:shadow-[0_0_0_3px_rgba(91,95,231,0.06)]'
          )}>
            {/* Reply / Note floating toggle — positioned above textarea, hidden when other elements occupy the space above */}
            {showReplyNoteToggle && (
              <div className="absolute -top-2 left-0 -translate-y-full z-20 flex items-center gap-0.5 bg-popover rounded-full px-1 py-0.5">
                <button
                  data-testid="chat-reply-tab"
                  type="button"
                  onClick={() => setNoteMode(false)}
                  className={cn(
                    'px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors',
                    !noteMode
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  )}
                >
                  Reply
                </button>
                <button
                  data-testid="chat-note-tab"
                  type="button"
                  onClick={() => { setNoteMode(true); setSelectedTemplate(null); }}
                  className={cn(
                    'px-2.5 py-0.5 rounded-full text-[11px] font-medium transition-colors',
                    noteMode
                      ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  )}
                >
                  Note
                </button>
              </div>
            )}
            {/* Textarea — hidden when template is selected */}
            {selectedTemplate ? (
              <div className="rounded-xl bg-muted/50 px-3 py-2.5 pr-20 text-xs text-muted-foreground min-h-[40px] flex items-center">
                Template: {selectedTemplate.name}
              </div>
            ) : (
              <>
                {/* Highlight backdrop for WhatsApp formatting + unresolved placeholders */}
                {(hasFormatting || hasUnresolvedPlaceholders) && (
                  <div
                    ref={highlightRef}
                    className="absolute inset-0 rounded-xl px-3.5 pt-2.5 pb-9 pr-12 text-sm whitespace-pre-wrap break-words overflow-hidden pointer-events-none"
                    style={{ wordBreak: 'break-word' }}
                    aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: highlightMarkup }}
                  />
                )}
                <textarea
                  data-testid="chat-input"
                  ref={textareaRef}
                  value={message}
                  onChange={handleInput}
                  onKeyDown={handleKeyDown}
                  onScroll={syncScroll}
                  onFocus={() => {
                    // Chat-view rule: gate on the bubble dot
                    // (realtimeMessageIds), not the inbox unread badge.
                    // Focusing the input on a chat with no fresh
                    // arrivals shouldn't fire a CF.
                    if (realtimeMessageIds.size > 0) {
                      markAsRead(conversation.id);
                      clearRealtimeMessageIds();
                    }
                  }}
                  placeholder={
                    noteMode
                      ? 'Type your notes...'
                      : availableProperties.length > 0
                        ? 'Type a message... (/ templates, # placeholders)'
                        : 'Type a message... (/ for templates)'
                  }
                  disabled={isDisabled}
                  rows={1}
                  className={cn(
                    'w-full rounded-xl border-none transition-colors resize-none',
                    isMini ? 'px-3 pt-2.5 pb-8 pr-10 text-sm' : 'px-3.5 pt-2.5 pb-9 pr-12 text-sm',
                    'focus:outline-none focus:ring-0',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    'placeholder:text-muted-foreground',
                    'max-h-[120px] min-h-[40px]',
                    noteMode
                      ? 'bg-amber-50/50 dark:bg-amber-950/20'
                      : (hasFormatting || hasUnresolvedPlaceholders)
                        ? 'text-transparent caret-foreground bg-transparent'
                        : 'bg-background'
                  )}
                />
              </>
            )}

            {/* # Placeholder dropdown */}
            {showPlaceholders && !selectedTemplate && (
              <div
                ref={placeholderDropdownRef}
                className="absolute bottom-full left-0 mb-2 w-72 rounded-lg border border-border bg-popover shadow-lg z-50 flex flex-col max-h-64"
              >
                {/* Sticky header with search */}
                <div className="px-2 pt-2 pb-1.5 border-b border-border shrink-0">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    <input
                      ref={placeholderSearchRef}
                      type="text"
                      value={placeholderSearch}
                      onChange={(e) => {
                        setPlaceholderSearch(e.target.value);
                        setSelectedPropIndex(0);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown') {
                          e.preventDefault();
                          setSelectedPropIndex((i) => Math.min(i + 1, filteredProperties.length - 1));
                        } else if (e.key === 'ArrowUp') {
                          e.preventDefault();
                          setSelectedPropIndex((i) => Math.max(i - 1, 0));
                        } else if (e.key === 'Enter' && filteredProperties.length > 0) {
                          e.preventDefault();
                          insertPlaceholderProperty(filteredProperties[selectedPropIndex]);
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setShowPlaceholders(false);
                          setPlaceholderSearch('');
                          textareaRef.current?.focus();
                        }
                      }}
                      placeholder={`Search ${activeCrmRecipient?.module || 'record'} fields...`}
                      className="w-full rounded-md border border-border bg-background pl-7 pr-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary/40 placeholder:text-muted-foreground"
                      autoFocus
                    />
                  </div>
                </div>

                {/* Scrollable field list */}
                <div className="overflow-y-auto flex-1 scrollbar-thin">
                  {filteredProperties.length === 0 ? (
                    <div className="px-3 py-4 text-center">
                      <p className="text-xs text-muted-foreground">No matching fields</p>
                    </div>
                  ) : (
                    filteredProperties.map((prop, idx) => (
                      <button
                        key={prop}
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          insertPlaceholderProperty(prop);
                        }}
                        className={cn(
                          'w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2',
                          idx === selectedPropIndex
                            ? 'bg-primary/10 text-primary'
                            : 'hover:bg-muted text-foreground'
                        )}
                      >
                        <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <span className="truncate flex-1">{fieldDisplayLabels[prop] || prop}</span>
                        <span className="text-[10px] text-muted-foreground shrink-0 max-w-[120px] truncate">
                          {propertyValues[prop] || `{{${prop}}}`}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* @ Mention dropdown (note mode only) */}
            {showMentions && noteMode && filteredMentionUsers.length > 0 && (
              <div
                ref={mentionDropdownRef}
                className="absolute bottom-full left-0 mb-2 w-64 rounded-lg border border-border bg-popover shadow-lg z-50 flex flex-col max-h-48"
              >
                <div className="px-3 py-1.5 border-b border-border shrink-0">
                  <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Mention a team member</span>
                </div>
                <div className="overflow-y-auto flex-1 scrollbar-thin">
                  {filteredMentionUsers.map((user, idx) => (
                    <button
                      key={user.email}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        insertMention(user.email, user.name);
                      }}
                      className={cn(
                        'w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2',
                        idx === selectedMentionIndex
                          ? 'bg-primary/10 text-primary'
                          : 'hover:bg-muted text-foreground'
                      )}
                    >
                      <div className="h-6 w-6 rounded-full bg-primary/10 text-primary text-[10px] font-bold flex items-center justify-center shrink-0">
                        {(user.name || user.email).charAt(0).toUpperCase()}
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-xs font-medium truncate">{user.name || user.email.split('@')[0]}</span>
                        <span className="text-[10px] text-muted-foreground truncate">{user.email}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Text formatting toolbar */}
            <TextFormattingToolbar
              visible={formattingToolbar.visible && !selectedTemplate}
              position={formattingToolbar.position}
              onFormat={formattingToolbar.applyFormat}
            />

            {/* Toolbar — bottom-left inside textarea */}
            <div className={cn("absolute flex items-center gap-0.5 z-10", isMini ? "-bottom-1 left-1" : "bottom-1.5 left-1.5", selectedTemplate && "hidden")} ref={emojiRef}>
              {/* Assignee avatar — compact picker, only in CHAT view */}
              {!isPlaceholder && activeView === 'CHAT' && (
                <ConvAssigneePicker
                  conversationId={conversation.id}
                  currentAssignee={conversation.assigneeEmail}
                  compact
                  botActive={!!conversation.activeFlow}
                  botLabel={conversation.activeFlow?.flowName}
                />
              )}

              {showEmojiPicker && (
                <button
                  data-testid="chat-emoji-btn"
                  type="button"
                  onClick={() => setShowEmoji(!showEmoji)}
                  className={cn(
                    'rounded transition-colors',
                    isMini ? 'p-1' : 'p-1.5',
                    showEmoji
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                >
                  <Smile className={isMini ? "h-3.5 w-3.5" : "h-4 w-4"} />
                </button>
              )}

              <button
                data-testid="chat-attachment-btn"
                type="button"
                onClick={handleAttachFile}
                className={cn(
                  'rounded transition-colors',
                  isMini ? 'p-1' : 'p-1.5',
                  attachedFile
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                <Paperclip className={isMini ? "h-3.5 w-3.5" : "h-4 w-4"} />
              </button>

              {/* Template button — shows when templates are available (saved or TPA).
                  Hidden in note mode: templates are provider messages, and a note
                  never reaches the provider. */}
              {!noteMode && (
                <button
                  data-testid="chat-template-btn"
                  type="button"
                  onClick={() => openTemplateDialog()}
                  disabled={templatesLoading}
                  className={cn(
                    'rounded transition-colors',
                    isMini ? 'p-1' : 'p-1.5',
                    selectedTemplate
                      ? 'text-primary bg-primary/10'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )}
                  title="Send template message"
                >
                  <FileText className={isMini ? "h-3.5 w-3.5" : "h-4 w-4"} />
                </button>
              )}

              {/* Emoji picker */}
              {showEmojiPicker && showEmoji && (
                <div data-testid="emoji-picker" className="absolute bottom-full left-0 mb-2 w-72 max-h-64 overflow-y-auto rounded-lg border border-border bg-popover p-3 shadow-lg z-50 scrollbar-thin">
                  {EMOJI_CATEGORIES.map((cat) => (
                    <div key={cat.label} className="mb-2 last:mb-0">
                      <p className="text-[10px] font-medium text-muted-foreground mb-1 sticky top-0 bg-popover py-0.5">{cat.label}</p>
                      <div className="flex flex-wrap gap-0.5">
                        {cat.emojis.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => insertEmoji(emoji)}
                            className="rounded p-1 text-base hover:bg-muted transition-colors"
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Schedule badge — floating above send area when schedule is set */}
            {scheduledAt && !noteMode && (
              <div className="absolute bottom-9 right-1.5 z-10 flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-medium">
                <Clock className="h-2.5 w-2.5" />
                {formatScheduleDate(scheduledAt)}
                <button
                  type="button"
                  onClick={() => setScheduledAt(null)}
                  className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20 transition-colors"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            )}

            {/* Send + Schedule + Mic buttons — bottom-right inside textarea */}
            <div className={cn("absolute z-10 flex items-center gap-1", isMini ? "-bottom-1 right-1" : "bottom-1.5 right-1.5")}>
              {/* Recording indicator bar */}
              {recording ? (
                <div className="flex items-center gap-1.5">
                  <span className="flex items-center gap-1 text-xs text-red-500 font-medium">
                    <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                    {Math.floor(recordingDuration / 60)}:{String(recordingDuration % 60).padStart(2, '0')}
                  </span>
                  <button
                    type="button"
                    onClick={cancelRecording}
                    className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                    title="Cancel recording"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                  {(() => {
                    const vBtn = (
                      <Button
                        onClick={handleSendVoice}
                        disabled={sending || !canSend}
                        size="icon-sm"
                        className="rounded-lg h-7 w-7 bg-red-500 hover:bg-red-600 text-white"
                      >
                        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      </Button>
                    );
                    return !canSend ? <Tooltip content={permissionsReady ? "No permission to send from this sender" : "Checking your permissions…"} side="top" className="left-auto right-0 translate-x-0">{vBtn}</Tooltip> : vBtn;
                  })()}
                </div>
              ) : (
                <>
                  {/* Schedule picker (clock icon) — hidden in note mode, Twilio only */}
                  {!noteMode && useAppStore.getState().appCodeTPA === 'twilio' && (
                    <SchedulePickerPopover
                      scheduledAt={scheduledAt}
                      onSchedule={setScheduledAt}
                      align="end"
                      side="top"
                      compact
                    />
                  )}

                  {/* Send button */}
                  {(() => {
                    const btn = (
                      <Button
                        data-testid="chat-send-btn"
                        onClick={handleSend}
                        disabled={isDisabled || (!canSend && !noteMode) || (!selectedTemplate && !message.trim() && !attachedFile) || !!hasUnfilledVars}
                        size="icon-sm"
                        className={cn(
                          'rounded-lg transition-all',
                          isMini ? 'h-6 w-6' : 'h-7 w-7',
                          noteMode
                            ? 'bg-amber-500 hover:bg-amber-600 hover:shadow-[0_0_8px_rgba(245,158,11,0.3)] text-white'
                            : scheduledAt
                              ? 'bg-primary/90'
                              : 'hover:shadow-[0_0_8px_rgba(91,95,231,0.3)]'
                        )}
                      >
                        {sending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : noteMode ? (
                          <StickyNote className="h-3.5 w-3.5" />
                        ) : scheduledAt ? (
                          <Clock className="h-3.5 w-3.5" />
                        ) : (
                          <Send className="h-3.5 w-3.5" />
                        )}
                      </Button>
                    );
                    return (!canSend && !noteMode) ? <Tooltip content={permissionsReady ? "No permission to send from this sender" : "Checking your permissions…"} side="top" className="left-auto right-0 translate-x-0">{btn}</Tooltip> : btn;
                  })()}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Template selection dialog — when 24h window expired, only show TPA provider templates (no custom) */}
      <TemplateDialog
        open={showTemplateDialog}
        onOpenChange={setShowTemplateDialog}
        templates={whatsAppWindowExpired ? tpaTemplates : templates}
        loading={templatesLoading}
        selected={selectedTemplate}
        onSelect={handleSelectTemplate}
        channel={conversation.channel}
        onSaveTemplate={saveTemplate}
        onDeleteTemplate={deleteTemplate}
        hideCustomActions={whatsAppWindowExpired}
        forceRefresh={forceRefreshTemplates}
        refreshing={tpaTemplatesLoading}
      />

      {/* Variable filling dialog — same as compose view */}
      {selectedTemplate && (
        <VariableFillingDialog
          open={showVariableDialog}
          onOpenChange={(open) => {
            setShowVariableDialog(open);
            if (!open) {
              // If closed without filling all variables, clear template
              const vars = selectedTemplate.variables || [];
              const allFilled = vars.length === 0 || vars.every((v) => templateVariables[v]?.trim());
              if (!allFilled) {
                clearTemplate();
              }
            }
          }}
          template={selectedTemplate}
          templateVariables={templateVariables}
          setTemplateVariable={setTemplateVariable}
        />
      )}
    </div>
  );
}
